<template>
    <div class="sign-in-container">
        <h1 class="title">{{ $t("signInUp.signIn") }}</h1>
        <form @submit.prevent="submitForm">
            <input type="email" v-model="email" @input="onEmailInput" :placeholder="$t('signInUp.emailAddress')" />
            <label for="password_reg" class="password-area">
                <input id="password_reg" :type="passType" v-model="password" :placeholder="$t('signInUp.password')" />
                <i :class="isPassHidden ? 'icon-eye-blocked' : 'icon-eye' " @click="showPassword" ></i>
            </label>
            <transition name="fade" mode="out-in">
                <div class="submit-container" v-if="!isPending">
                    <input type="submit" :disabled="formInvalid" :value="$t('general.validate')" />

                    <div class="delimiter">
                        <hr />
                        <span>
                            {{ $t("general.or").toUpperCase() }}
                        </span>
                        <hr />
                    </div>

                    <div class="google-btn" @click.prevent="googleSignIn">
                        <div class="google-icon-wrapper">
                            <img class="google-icon" :src="require('@/assets/Google_Logo.svg')" />
                        </div>
                        <p class="btn-text">
                            {{ $t("signInUp.googleSignIn") }}
                        </p>
                    </div>

                    <router-link
                        to="SignUp"
                        class="signup-link"
                        v-if="qrcodeObject.isOpen"
                        @click.prevent="$router.replace('/SignUp')"
                        >{{ $t("signInUp.createAccount") }}</router-link
                    >
                    <router-link to="ForgetPassword" class="forget-password-link">{{
                        $t("signInUp.forgetPasswordTitle")
                    }}</router-link>

                    <RemindsMeCheckbox :value="remindsMe" @input="updateRemindsMe" />
                </div>
                <Loader class="loader" v-else
            /></transition>
            <transition name="fade">
                <div v-if="error && error.length > 0" class="error-message">
                    {{ error }}
                </div>
            </transition>
        </form>
        <div class="view-footer">
            <RGPDLink />
            <GcuLink class="gcu-link" v-if="$t('general.GCU').length" />
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";

import Loader from "@/components/Loader.vue";
import RGPDLink from "@/components/RGPDLink.vue";
import GcuLink from "@/components/gcuLink.vue";
import { State } from "@/store";
import { mapState } from "vuex";
import RemindsMeCheckbox from "@/components/RemindsMeCheckbox.vue";

export default defineComponent({
    name: "Signup",
    components: {
        Loader,
        GcuLink,
        RGPDLink,
        RemindsMeCheckbox
    },
    data() {
        return {
            email: "",
            password: "",
            passType: `password`,
            isPassHidden: true,
            isPending: false,
            error: "",
            remindsMe: false
        };
    },
    computed: {
        formInvalid() {
            return !this.email.length || !this.password.length;
        },
        ...mapState({
            authenticated: (state: State) => state.auth.authenticated,
            qrcodeObject: (state: State) => state.qrcode.qrcodeObject
        })
    },
    watch: {
        async authenticated() {
            if (this.authenticated) {
                this.isPending = false;

                // if logged with wrong account
                if (
                    !this.qrcodeObject.isOpen &&
                    this.$store.state.auth.userId !== this.qrcodeObject.experiences[0].ownerId
                ) {
                    this.error = this.$t("signInUp.notQrOwner");
                    await this.$store.dispatch("auth/logout");
                } else {
                    this.$router.replace("Upload");
                }
            }
        }
    },
    methods: {
        showPassword() {
          if (this.isPassHidden) {
                this.passType = `text`;
                this.isPassHidden = false;
            } else {
                this.passType = `password`;
                this.isPassHidden = true;
            }
        },
        onEmailInput(ev: Event) {
            const input = ev.target as HTMLInputElement;
            input.value = input.value.toLowerCase();
        },
        async submitForm() {
            this.error = "";
            this.isPending = true;
            try {
                await this.$store.dispatch("auth/login", {
                    email: this.email,
                    password: this.password,
                    remindsMe: this.remindsMe
                });
            } catch (e) {
                this.isPending = false;
                this.error = this.$t("signInUp." + e.message);
            }
        },

        updateRemindsMe(value) {
            this.remindsMe = value;
        },

        async googleSignIn() {
            try {
                const googleUser = await this.$gAuth.signIn();

                if (!googleUser) {
                    throw new Error("User is null");
                }

                const token = googleUser.getAuthResponse().id_token;
                await this.$store.dispatch("auth/googleSignIn", token);
            } catch (e) {
                if (e.error === "popup_closed_by_user") {
                    return;
                }

                if (e?.message?.length) {
                    this.error = this.$t("signInUp." + e.message);
                } else {
                    this.error = this.$t("signInUp.unknownError");
                }
            }
        }
    }
});
</script>

<style scoped>
h1 {
    margin: 0;
    color: var(--screenTextColor);
}

.sign-in-container,
.sign-in-container form {
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 15px;
    align-items: center;
    justify-content: center;
}

input[type="email"],
input[type="password"],
input[type="text"] {
    box-sizing: border-box;
    max-width: 185px;
    padding: 10px 20px;
    border-radius: calc(1em + 10px);
    line-height: 1em;
    background: var(--white);
    color: var(--black);
    border: 0;
    margin-left: auto;
    margin-right: auto;
    box-shadow: 2px 3px 5px 0px rgb(150 150 150 / 50%);
}

input[type="password"],
input[type="text"] {
    padding-right: 40px;
}
.password-area{
    margin: 0 auto;
    max-width: 185px;
    position: relative;
}

.icon-eye-blocked,
.icon-eye {
    content: '';
    position: absolute; 
    top: 10px;
    right: 14px;
    border-radius: 50%;
    transition: background-color .3s;
}

.icon-eye:hover,
.icon-eye-blocked:hover {
    background-color: rgba(0, 0, 0, 0.1);
}

.submit-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

input[type="submit"] {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    margin-bottom: 5px;
    padding: 10px 20px;
    border-radius: calc(1em + 10px);
    line-height: 1em;
    border: 0;
    background: var(--secondary);
    color: var(--buttonTextColor);
    transition: filter 0.2s ease-in;
}

input[type="submit"]:disabled {
    opacity: 0.5;
}

.signup-link,
.forget-password-link {
    margin-bottom: 10px;
    color: var(--screenTextColor);
}

.loader {
    margin: auto;
}

.error-message {
    color: var(--red);
}

.bounce-enter-active {
    animation: bounce-in 0.5s;
}
.bounce-leave-active {
    animation: bounce-in 0.5s reverse;
}
@keyframes bounce-in {
    0% {
        transform: scale(0);
    }
    50% {
        transform: scale(1.5);
    }
    100% {
        transform: scale(1);
    }
}

.view-footer {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
}

.google-btn {
    display: flex;
    flex-direction: row;
    height: 42px;
    background-color: #4285f4;
    border-radius: 2px;
    margin-bottom: 30px;
}

.google-btn:active {
    background: #1669f2;
}

.google-btn .google-icon-wrapper {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    /* position: absolute; */
    margin-top: 1px;
    margin-left: 1px;
    width: 40px;
    height: 40px;
    border-radius: 2px;
    background-color: var(--white);
}

.google-btn .google-icon {
    width: 18px;
    height: 18px;
}

.google-btn .btn-text {
    padding: 0 10px;
    color: var(--white);
    font-size: 14px;
    letter-spacing: 0.2px;
    font-family: "Roboto";
}

.delimiter {
    display: flex;
    flex-direction: row;
    padding-left: 15px;
    padding-right: 15px;
    width: calc(100% - 30px);
    margin-top: 10px;
    margin-bottom: 10px;
}

.delimiter hr {
    flex-grow: 1;
    height: 1px;
    border: 0;
    border-top: 1px solid var(--screenTextColor);
    opacity: 0.6;
}

.delimiter span {
    flex-grow: 0;
    margin-left: 5px;
    margin-right: 5px;
    font-weight: bold;
    color: var(--screenTextColor);
}

@import url(https://fonts.googleapis.com/css?family=Roboto:500);
</style>
